import { Box, Flex, Text } from "@chakra-ui/react";
import {
  CHAT_RESPONSE_STREAM_TIME,
  CHAT_STATUS_GENERAL,
  DOCUMENT_TYPE,
  DRAWING_ANALYSIS_CHAT_STATUS,
  ERROR_TYPES,
  PAYLOAD_LANGUAGE,
  QUERY_TYPE,
  RESPONSE_STATUS,
  ROUTE_NAMES,
} from "@root/constants";
import { ChatError } from "../../chatError";
import { PDFIcon } from "@root/assets/icons/PDFIcon";
import { KPSLogoBig, QuestionLogo } from "@root/assets/icons";
import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  getSelectedModel,
  getThreadList,
} from "@root/store/selectors/entities/thread";
import { useDispatch, useSelector } from "react-redux";
import {
  doQuery,
  doQueryDrawingAnalysis,
  doQueryKCS,
  doQueryMultiDoc,
  doQueryProject,
} from "@root/store/thunks/chatThunks";
import { getDocumentById } from "@root/store/thunks/documentsActions";
import { useMainChatContext } from "@root/context/kps-chat-context";
import MarkdownContent from "@root/components/markdown/markdown";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RenderStatus from "./RenderStatus";
import BlinkingCursor from "@root/components/loader/BlinkingCursor";
import { useTranslation } from "react-i18next";
import useTypewriterEffect from "./useTypewriterEffect";
import ChatSources from "../../sources/ChatSources";
import { ChatModeQuestionIcon } from "@root/assets/icons/ChatModeQuestionIcon";
import { ChatLoader } from "../../chatLoader";
import ChatSourceData from "@root/pages/dashboard/components/ChatSourceData";
import { useProjectsContext } from "@root/context/projects-context";
import ChatFollowUpQuestions from "@root/pages/dashboard/components/ChatFollowUpQuestions";

function SystemChat({ chat, uid, chatInput, mode }: any) {
  const {
    currentMessageResponse,
    setCurrentMessageResponse,
    setIsMessaging,
    input,
    setIsWritingFirstPatch,
    urls,
    urlType,
    url,
    setUrls,
    setUrlType,
    setUrl,
    deepSearch,
    modeState,
    chatMode,
    currentPathname,
    getSource,
    getMode,
    getFilesPath,
    getDocNames,
    getPdfPageNum,
    downloadPdfOfThread,
  } = useMainChatContext();
  const { getFilename } = useProjectsContext();
  const { pathname } = useLocation();
  const isKCS = pathname.includes("kcs");

  const { error, editLoading } = chat;

  const output = chat?.output || chat?.meta?.answer;
  const [answer, setAnswer] = useState(output || "");
  console.log("chat: ", chat);

  const transformedAnswer = useMemo(() => {
    if (Array.isArray(answer) && answer?.length > 0) {
      // Convert array answers to pure markdown with file names as headers and content in blockquotes
      const arrayContent = answer
        ?.map((item, index) => {
          const fileName = item?.doc_name;
          // Ensure proper blockquote formatting by adding > to each line
          const docAnswer =
            typeof item?.doc_answer === "string"
              ? item.doc_answer
              : String(item?.doc_answer || "");
          const blockquoteContent = docAnswer
            ?.split("\n")
            ?.map((line: any) => (line?.trim() === "" ? ">" : `> ${line}`))
            ?.join("\n");

          // Add source information within blockquote content using pure markdown
          const sourceInfo = item?.source
            ? `\n\n> **Source:** \`${item.source}\``
            : "";

          return `#### 📄 ${fileName}\n\n${blockquoteContent}${sourceInfo}`;
        })
        ?.join("\n\n---\n\n");

      // Add Export Checklist button at the end using pure markdown
      const exportButton = `\n\n> \n> \`\`\`\n> 📄 Export Checklist\n> \`\`\``;

      // Wrap entire response in a blockquote for blue left border (ONLY for array answers)
      return `> ${arrayContent
        ?.split("\n")
        ?.map((line) => (line?.trim() === "" ? "" : line))
        ?.join("\n> ")}${exportButton}`;
    }

    // For single answers, return as-is without blockquote wrapper
    return answer;
  }, [answer]);

  const [sources, setSources] = useState(chat?.meta?.sources || []);
  const [followUpQuestions, setFollowUpQuestions] = useState(
    chat?.meta?.follow_up || []
  );
  const [currentInterval, setCurrentInterval] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState(output || "");
  const [currentStatus, setCurrentStatus] = useState(output ? "stop" : "null");
  const [isMessageCompleted, setIsMessageCompleted] = useState(false);
  const [index, setIndex] = useState(0);
  const typingTimeoutRef: any = useRef(null);
  const isProcessingNewText = useRef(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const threadList: any = useSelector(getThreadList);
  const currentChat: any = threadList[threadList?.length - 1];
  const [currentError, setCurrentError] = useState<any>(null);
  const [currentID, setCurrentID] = useState(null);
  const chatKuId = chat?.meta?.chatKuid || chat?.meta?.kuid;
  const userInput =
    input ||
    currentMessageResponse?.question ||
    threadList?.[threadList?.length - 1]?.input ||
    threadList?.[threadList?.length - 2]?.input;
  const lastChat = threadList?.[threadList?.length - 1];
  const navigate = useNavigate();
  const { id } = useParams();
  const modelOption = useSelector(getSelectedModel);
  const currentChatKuid = currentMessageResponse?.chatKuid || chatKuId;
  const dispatch = useDispatch();

  const searchStatus = currentMessageResponse?.search_status;
  const webpageStatus = currentMessageResponse?.webpage_status;
  const fileStatus = currentMessageResponse?.file_status;
  const youtubeStatus = currentMessageResponse?.youtube_status;
  const deepSearchStatus = currentMessageResponse?.deepsearch_status;
  const type = currentMessageResponse?.type;
  const { t } = useTranslation("common");
  const chatItem = chat?.meta?.c || chat?.meta;
  const currentLanguage = localStorage?.getItem("i18nextLng")?.toUpperCase();
  const { documentData } = useProjectsContext();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Custom link click handler
  const handleLinkClick = useCallback(
    (text: string, url: string) => {
      console.log("Link clicked:", { text, sources });
      if (sources?.length) {
        const currentDocumentId = text?.split("_")?.[0];
        const sourceItem = sources?.find(
          (item: any) => item?.documentId === currentDocumentId
        );
        if (sourceItem) {
          window.open(sourceItem?.signedUrl, "_blank");
        }
      }
    },
    [sources]
  );

  const CHAT_STATUS = useMemo(() => {
    // if (currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
    //   return DRAWING_ANALYSIS_CHAT_STATUS;
    // }
    return CHAT_STATUS_GENERAL;
  }, [currentPathname]);

  // Centralized Status Management
  const statusManager = useMemo(() => {
    const chatMetaStatus = chat?.meta?.status;

    const currentMessageStatus = currentMessageResponse?.status;
    const chatItemStatus = chatItem?.status;

    // Primary status from chat meta or current message response
    const primaryStatus = currentMessageStatus || chatMetaStatus;

    return {
      // Status constants for easy reference
      STATUS: CHAT_STATUS,

      // Current status values
      current: {
        chatMeta: chatMetaStatus,
        messageResponse: currentMessageStatus,
        chatItem: chatItemStatus,
        primary: primaryStatus,
      },

      // Status checks
      isProcessing: () => CHAT_STATUS.PROCESSING.includes(primaryStatus),
      isCompleted: () =>
        primaryStatus === CHAT_STATUS.COMPLETED ||
        chatItemStatus === CHAT_STATUS.COMPLETED,
      hasError: () =>
        primaryStatus === CHAT_STATUS.ERROR ||
        chatItemStatus === CHAT_STATUS.ERROR ||
        currentError !== null ||
        error !== undefined,
      isPending: () => primaryStatus === CHAT_STATUS.PENDING,
      isInProgress: () => CHAT_STATUS.IN_PROGRESS.includes(primaryStatus),
      isNearComplete: () => primaryStatus === CHAT_STATUS.NEAR_COMPLETE,

      // Loading state check
      isLoading: () =>
        chatItemStatus !== CHAT_STATUS.COMPLETED &&
        chatItemStatus !== CHAT_STATUS.NEAR_COMPLETE &&
        chatItemStatus !== CHAT_STATUS.ERROR,

      // Error state check
      isError: () => chatItemStatus === CHAT_STATUS.ERROR,

      // Specific processing status checks
      isDeepSearchProcessing: () =>
        type === RESPONSE_STATUS.DEEP_SEARCH && deepSearchStatus === 1,
      isSearchProcessing: () =>
        type === RESPONSE_STATUS.SEARCH &&
        [CHAT_STATUS.PENDING, 1].includes(searchStatus),
      isWebProcessing: () =>
        type === RESPONSE_STATUS.WEBPAGE &&
        CHAT_STATUS.IN_PROGRESS.includes(webpageStatus),
      isFileProcessing: () =>
        type === RESPONSE_STATUS.FILE &&
        [...CHAT_STATUS.IN_PROGRESS, CHAT_STATUS.ERROR].includes(fileStatus),
      isYoutubeProcessing: () =>
        type === RESPONSE_STATUS.YOUTUBE &&
        [...CHAT_STATUS.IN_PROGRESS, CHAT_STATUS.ERROR].includes(youtubeStatus),

      // Combined processing check
      isAnyProcessing: () =>
        (type === RESPONSE_STATUS.DEEP_SEARCH && deepSearchStatus === 1) ||
        (type === RESPONSE_STATUS.SEARCH &&
          [CHAT_STATUS.PENDING, 1].includes(searchStatus)) ||
        (type === RESPONSE_STATUS.WEBPAGE &&
          CHAT_STATUS.IN_PROGRESS.includes(webpageStatus)) ||
        (type === RESPONSE_STATUS.FILE &&
          [...CHAT_STATUS.IN_PROGRESS, CHAT_STATUS.ERROR].includes(
            fileStatus
          )) ||
        (type === RESPONSE_STATUS.YOUTUBE &&
          [...CHAT_STATUS.IN_PROGRESS, CHAT_STATUS.ERROR].includes(
            youtubeStatus
          )),

      // Should continue polling
      shouldContinuePolling: () => {
        const isProcessing = CHAT_STATUS.IN_PROGRESS.includes(primaryStatus);
        const isCompleted = primaryStatus === CHAT_STATUS.COMPLETED;
        const hasError = primaryStatus === CHAT_STATUS.ERROR;
        return isProcessing || (!isCompleted && !hasError);
      },

      // Should start typing animation
      shouldStartTyping: () =>
        primaryStatus !== CHAT_STATUS.COMPLETED &&
        primaryStatus !== CHAT_STATUS.NEAR_COMPLETE &&
        currentStatus !== "start",

      // Should set completed state
      shouldSetCompleted: () => primaryStatus === CHAT_STATUS.COMPLETED,

      // Should navigate after completion
      shouldNavigateAfterCompletion: () =>
        primaryStatus === CHAT_STATUS.COMPLETED && !id,

      // Should fetch sources
      shouldFetchSources: () => primaryStatus === CHAT_STATUS.COMPLETED,

      // Should stop polling
      shouldStopPolling: () =>
        chatMetaStatus === CHAT_STATUS.ERROR ||
        chatMetaStatus === CHAT_STATUS.COMPLETED,

      // Should set answer and typed answer
      shouldSetAnswerAndTyped: () =>
        chat?.output && chatMetaStatus === CHAT_STATUS.COMPLETED,
    };
  }, [
    chat?.meta?.status,
    currentMessageResponse?.status,
    chatItem?.status,
    type,
    deepSearchStatus,
    searchStatus,
    webpageStatus,
    fileStatus,
    youtubeStatus,
    id,
    chat?.output,
    CHAT_STATUS,
  ]);

  function handleCursorVisibility() {
    if (statusManager.isAnyProcessing() || currentStatus === "start") {
      return false;
    }
    return currentStatus === "start";
  }

  const isCursorVisible = handleCursorVisibility();

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentStatus === "start") {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (index < answer?.length) {
        typingTimeoutRef.current = setTimeout(() => {
          setTypedAnswer((prev: any) => prev + answer[index]);
          setIndex((prev) => prev + 1);
        }, 5);
      } else if (isMessageCompleted) {
        setCurrentStatus("stop");
        setUrls([]);
        setUrlType(null);
      }
    }
  }, [index, answer, currentStatus, isMessageCompleted]);

  useEffect(() => {
    if (isProcessingNewText.current || !answer) return;
    isProcessingNewText.current = true;
    try {
      if (statusManager.isCompleted() || statusManager.isNearComplete()) {
        setTypedAnswer(answer);
      } else if (answer?.length > typedAnswer?.length) {
        if (currentStatus !== "start") {
          setTypedAnswer("");
          setIndex(0);
          setCurrentStatus("start");
        }
      }
    } finally {
      isProcessingNewText.current = false;
    }
  }, [answer, statusManager.current.primary]);

  const fetchChat = useCallback(() => {
    console.log("fetchChat called");
    if (
      !chat?.chatId &&
      !currentMessageResponse?.chatId &&
      !currentMessageResponse?.id
    ) {
      return;
    }
    if (statusManager.isCompleted()) {
      return;
    }
    setCurrentError(null);
    if (
      !statusManager.isCompleted() &&
      !statusManager.isNearComplete() &&
      currentStatus !== "start"
    ) {
      setCurrentStatus("start");
    }

    const payload = {
      payload: {
        query: userInput,
        threadId: currentMessageResponse?.threadId || undefined,
        // model: modelOption?.value,
        // image_s3_url: urls?.[0]?.fileUrl,
        // url_type: urlType?.value,
        // url: urlType ? url : null,
        // query_type: deepSearch,
        // language:
        //   currentLanguage === "EN"
        //     ? PAYLOAD_LANGUAGE.ENGLISH
        //     : PAYLOAD_LANGUAGE.KOREAN,
        source: getSource(),
        mode: getMode()?.toString(),
        project_id: getMode(),
        s3_file_path: getFilesPath(),
        doc_name: getDocNames(),
        fileName: getFilename(),
        page_no: getPdfPageNum(),
        pdf_id: documentData?.id,
        chatId:
          chat?.chatId ||
          currentMessageResponse?.chatId ||
          currentMessageResponse?.id,
        // local: modeState,
      },
      onError: (error: any) => {
        setCurrentStatus("null");
        setCurrentError(error);
        setIsMessaging(false);
        setIsWritingFirstPatch(false);
      },
      onSuccess: (data: any) => {
        setIsMessageCompleted(data?.status === statusManager.STATUS.COMPLETED);
        setCurrentMessageResponse(data);
        setCurrentID(data?.kuid);
        if (data?.status === statusManager.STATUS.COMPLETED) {
          setIsMessaging(false);
          setCurrentInterval(0);
          if (data?.threadId && !id) {
            if (currentPathname?.includes(DOCUMENT_TYPE.DOCUMENT)) {
              navigate(`/document-chat/${data?.threadId}`, { replace: true });
            } else if (currentPathname?.includes(DOCUMENT_TYPE.CHECKLIST)) {
              navigate(`/checklist-chat/${data?.threadId}`, { replace: true });
            } else if (
              currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)
            ) {
              navigate(`/drawing-analysis-chat/${data?.threadId}`, {
                replace: true,
              });
            } else if (currentPathname?.includes(ROUTE_NAMES.KCS)) {
              navigate(`/kcs-chat/${data?.threadId}`, {
                replace: true,
              });
            } else if (currentPathname?.includes(ROUTE_NAMES.ASK_AI_CHATBOT)) {
              navigate(`/askAiFlow/Chat/${data?.threadId}`, {
                replace: true,
              });
            } else {
              navigate(`/chat/${data?.threadId}`, { replace: true });
            }
          }
        }
        if (
          data?.answer !== "null" ||
          (data?.url_type === "WEBSITE" && data?.webpage_summary) ||
          (data?.url_type === "PDF" && data?.description) ||
          (data?.url_type === "YOUTUBE" && data?.title)
        ) {
          let ans: any = "";
          if (data?.url_type === "WEBSITE") {
            ans = data?.webpage_summary;
          } else if (data?.url_type === "PDF") {
            ans = data?.description;
          } else if (data?.url_type === "YOUTUBE") {
            const youtubeStatus = data?.youtube_status;
            if (youtubeStatus === CHAT_STATUS.ERROR) {
              ans = answer + data?.table_of_contents;
            } else if (youtubeStatus === CHAT_STATUS.COMPLETED) {
              ans = answer + data?.summary;
            } else if (youtubeStatus === CHAT_STATUS.ERROR) {
              ans = answer + data?.detailed_summary;
            }
          } else if (data?.query_type === "DEEP_SEARCH") {
            const logsData = Object.values(data?.logs ?? {})
              .filter(
                (value) => typeof value === "string" && value.trim() !== ""
              )
              .join(" ");
            ans = logsData;
          } else {
            try {
              if (
                data?.answer &&
                data.answer !== "null" &&
                data.answer !== "undefined"
              ) {
                const parsedAnswer = JSON.parse(data.answer);
                if (parsedAnswer && typeof parsedAnswer === "object") {
                  ans = JSON.parse(parsedAnswer)?.response || "";
                } else {
                  ans = data.answer;
                }
              } else {
                ans = data?.answer !== "null" ? data?.answer : "";
              }
            } catch (e) {
              ans = data?.answer !== "null" ? data?.answer : "";
            }
          }
          if (ans?.startsWith("{")) {
            try {
              ans = JSON.parse(ans)?.answer || "";
            } catch (e) {
              // Keep original ans if parsing fails
            }
          }
          setAnswer(ans);
          if (data?.status === statusManager.STATUS.COMPLETED) {
            setTypedAnswer(ans);
            try {
              const fetchedSources = JSON.parse(data?.answer || "{}")?.sources;
              const fetchedFollowUpQuestions = JSON.parse(
                data?.answer || "{}"
              )?.follow_up;
              setSources(fetchedSources);
              setFollowUpQuestions(fetchedFollowUpQuestions);
            } catch (e) {
              setSources([]);
              setFollowUpQuestions([]);
            }
          }
          setCurrentMessageResponse({ ...currentMessageResponse, ...data });
          setIsWritingFirstPatch(false);
        }
      },
    };
    if (currentPathname?.includes(ROUTE_NAMES.KCS)) {
      dispatch(doQueryKCS(payload));
    } else if (currentPathname?.includes(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
      dispatch(doQueryDrawingAnalysis(payload));
    } else if (currentPathname?.includes("document")) {
      dispatch(doQueryProject(payload));
    } else if (currentPathname?.includes("checklist")) {
      dispatch(doQueryMultiDoc(payload));
    } else {
      dispatch(doQuery(payload));
    }
  }, [
    chat?.chatId,
    currentMessageResponse?.chatId,
    currentMessageResponse?.id,
    currentMessageResponse?.threadId,
    statusManager,
    currentStatus,
    userInput,
    getSource,
    getMode,
    getFilesPath,
    getDocNames,
    getFilename,
    getPdfPageNum,
    documentData?.id,
    currentPathname,
    dispatch,
    setCurrentError,
    setCurrentStatus,
    setIsMessaging,
    setIsWritingFirstPatch,
    setCurrentMessageResponse,
    setCurrentID,
    setCurrentInterval,
    setIsMessageCompleted,
    setAnswer,
    setTypedAnswer,
    setSources,
    answer,
    id,
    navigate,
  ]);

  const currentResponseId =
    currentMessageResponse?.chatId || currentMessageResponse?.id;
  const currentChatId = chat?.meta?.id || chat?.meta?.c?.id;
  const isChatIdEqual =
    !!(currentResponseId && currentChatId) &&
    currentResponseId === currentChatId;

  const { displayText, isComplete } = useTypewriterEffect(
    transformedAnswer || "",
    {
      delay: 2, // Speed of typing
      disableAnimation: !isChatIdEqual,
    }
  );

  // Add click listener for Export Checklist button
  useEffect(() => {
    const handleExportClick = (event: Event) => {
      const target = event.target as HTMLElement;

      // Check if the clicked element contains the export button text
      if (target.textContent?.includes("📄 Export Checklist")) {
        // Prevent event bubbling to avoid multiple handlers
        event.stopPropagation();

        const threadId = chat?.meta?.threadId || chat?.meta?.c?.threadId;
        const chatId = chat?.meta?.id || chat?.meta?.c?.id;
        if (threadId && chatId) {
          downloadPdfOfThread({
            threadId: threadId,
            chatId: chatId,
          });
        }
      }
    };

    // Add event listener to the chat container instead of document
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener("click", handleExportClick);
    }

    // Cleanup
    return () => {
      if (container) {
        container.removeEventListener("click", handleExportClick);
      }
    };
  }, [displayText, chat?.meta?.id, chat?.meta?.c?.id]); // Include chat IDs in dependencies

  useEffect(() => {
    if (chat?.output && statusManager.isCompleted()) {
      setAnswer(chat?.output);
      setTypedAnswer(chat?.output);
      setCurrentStatus("stop");
    }
  }, [chat?.output, statusManager.current.primary]);

  const isError = statusManager.isError();

  useEffect(() => {
    console.log("use effect test");
    if (chatKuId !== currentChatKuid || currentError || error || isError) {
      console.log("Polling stopped: chatKuId mismatch, error, or isError");
      return;
    }

    // For existing threads, don't poll if already completed
    if (statusManager.isCompleted() || statusManager.hasError()) {
      console.log("Polling stopped: thread already completed");
      return;
    }

    if (statusManager.shouldContinuePolling()) {
      console.log("Starting/continuing polling...");

      // Only start polling if not already completed or errored
      if (!statusManager.shouldStopPolling()) {
        if (CHAT_RESPONSE_STREAM_TIME.FIRST_REFETCH_WAIT < currentInterval) {
          console.log("Fetching chat...");
          fetchChat();
        }

        pollingIntervalRef.current = setInterval(() => {
          console.log("chat?.meta?.c?.status", chat?.meta?.c?.status);
          fetchChat();
        }, CHAT_RESPONSE_STREAM_TIME.REFETCH_INTERVAL);
      }
    } else {
      console.log("Polling stopped: completed or error status");
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [
    currentChat?.meta?.status,
    error,
    currentError,
    chatKuId,
    currentChatKuid,
    isError,
    statusManager,
  ]);

  const isLoading = statusManager.isLoading();
  if (isLoading) {
    return (
      <Flex justifyContent={"center"} zIndex={9}>
        <Box marginLeft={"150px"} marginTop={"110px"}>
          <ChatLoader />
        </Box>
      </Flex>
    );
  }

  if (isError) {
    return (
      <Box
        w="full"
        bg={"blue.100"}
        border={"1px solid gray"}
        p={"15px"}
        rounded={"10px"}
      >
        Something went wrong! Please retry.
      </Box>
    );
  }
  return (
    <Box
      ref={chatContainerRef}
      position="relative"
      display="flex"
      flexDir="column"
      gap="16px"
    >
      {/* AI Chat */}
      {/* Question */}
      <Box>
        <Flex>
          <Box pl="10px">
            <RenderStatus
              currentMessageResponse={currentMessageResponse}
              currentStatus={currentStatus}
              chat={chat}
              isDeepSearchProcessing={statusManager.isDeepSearchProcessing()}
              isSearchProcessing={statusManager.isSearchProcessing()}
              isWebProcessing={statusManager.isWebProcessing()}
              isFileProcessing={statusManager.isFileProcessing()}
              isYoutubeProcessing={statusManager.isYoutubeProcessing()}
            />
            <Flex gap="6px" align="flex-end" flexWrap="wrap">
              {displayText?.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    color: "theme.gray.500",
                    wordBreak: "break-word",
                    width: "100%",
                  }}
                  color={"theme.gray.500"}
                >
                  <div
                    style={{
                      marginTop: "-13px",
                      marginLeft: "-10px",
                      width: "100%",
                    }}
                    data-chat-message={chat?.meta?.id || chat?.meta?.c?.id}
                  >
                    {isKCS && <KPSLogoBig />}
                    <MarkdownContent
                      content={displayText}
                      onLinkClick={handleLinkClick}
                    />
                  </div>
                </div>
              )}
              {isCursorVisible && !isComplete && <BlinkingCursor />}
            </Flex>
          </Box>
        </Flex>
      </Box>
      {sources?.length > 0 && (
        <Flex flexDirection="row" justifyContent="flex-start">
          <ChatSourceData sources={sources ? sources : []} />
        </Flex>
      )}
      {followUpQuestions?.length > 0 && (
        <ChatFollowUpQuestions followUpQuestions={followUpQuestions} />
      )}
      {/* <ChatSources /> */}
    </Box>
  );
}

export default SystemChat;
